/**
 * EnterpriseLayout Component
 *
 * Dedicated layout for Enterprise Portal (enterprise_admin, enterprise_user)
 * Completely separate from clinical workspace, partner portal, and management console
 *
 * Theme: Amber/Orange (#f59e0b / #d97706)
 * Features: Channel Partners, Organizations (view-only), Users, Analytics, Settings
 *
 * IMPORTANT: Enterprise users cannot access clinical data (PHI) - HIPAA compliance
 */

import { Outlet, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  Building2,
  Users,
  Settings,
  LogOut,
  BarChart3,
  Menu,
  X,
  CreditCard,
  HelpCircle,
  Bell,
  Building,
  Briefcase
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const EnterpriseLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Dismiss on outside pointer-down rather than with a full-screen overlay: the overlay sits
  // outside the sticky header's stacking context and swallows the click. Moving it inside the header
  // is not an option either -- the header's backdrop-blur makes it the containing block for fixed
  // children, which shrinks the overlay to the header's box.
  useEffect(() => {
    if (!showUserMenu) return;

    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsidePointer);
    return () => document.removeEventListener('mousedown', closeOnOutsidePointer);
  }, [showUserMenu]);

  // Only enterprise roles can access Enterprise Portal
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!['enterprise_admin', 'enterprise_user'].includes(user.role)) {
    // Non-enterprise users should use appropriate workspace
    if (user.role === 'superadmin') {
      return <Navigate to="/management" replace />;
    }
    if (['partner_admin', 'partner_user', 'channel_partner_admin', 'channel_partner_user'].includes(user.role)) {
      return <Navigate to="/partner" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user.role === 'enterprise_admin';

  const navigation = [
    { name: 'Dashboard', href: '/enterprise', icon: BarChart3 },
    { name: 'Channel Partners', href: '/enterprise/partners', icon: Briefcase },
    { name: 'Organizations', href: '/enterprise/organizations', icon: Building2 },
    { name: 'Users', href: '/enterprise/users', icon: Users },
    { name: 'Usage & Billing', href: '/enterprise/usage', icon: CreditCard },
    ...(isAdmin ? [{ name: 'Settings', href: '/enterprise/settings', icon: Settings }] : [])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 w-full overflow-x-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <span className="text-xl font-bold text-amber-600">Enterprise Portal</span>
          <button onClick={() => setSidebarOpen(false)} className="p-2">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        <nav className="px-4 py-6 space-y-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-amber-100 text-amber-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-40 md:w-64 md:flex md:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Building className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Enterprise Portal</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/enterprise'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Help & Support */}
          <div className="px-4 py-4 border-t border-gray-200">
            <a
              href="mailto:enterprise-support@amplify.health"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <HelpCircle className="h-5 w-5" />
              Help & Support
            </a>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="md:pl-64 w-full overflow-x-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="h-6 w-6 text-gray-500" />
            </button>

            {/* Enterprise info badge */}
            <div className="hidden md:flex items-center gap-2">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                {user.role === 'enterprise_admin' ? 'Enterprise Admin' : 'Enterprise User'}
              </span>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <button className="p-2 rounded-lg hover:bg-gray-100 relative">
                <Bell className="h-5 w-5 text-gray-500" />
              </button>

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100"
                >
                  <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-amber-700">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <NavLink
                      to="/enterprise/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8 w-full overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full overflow-x-hidden">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default EnterpriseLayout;
